import { IonContent, IonPage } from '@ionic/react';
import { useCallback, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import CompareInsightsPicker from './CompareInsightsPicker';
import DefaultBehaviorToast from './DefaultBehaviorToast';
import SettingsHeader from './SettingsHeader';
import {
  SETTING_DEFINITIONS,
  SettingOption,
  isOptionSettingKey,
  isSameSettingValue,
} from './settingsOptions';
import { useCasinoOptions } from './useCasinoOptions';
import { useUserSettings } from './useUserSettings';

// One App Defaults setting. Picking an option saves it but stays on the page;
// the user leaves with the back button.
const SettingOptions: React.FC = () => {
  const { settingKey } = useParams<{ settingKey: string }>();
  const { settings, defaults, updateSetting, resetSettings, visibility } = useUserSettings();
  const casinoOptions = useCasinoOptions();
  const [showDefaultToast, setShowDefaultToast] = useState(false);
  const closeDefaultToast = useCallback(() => setShowDefaultToast(false), []);

  if (!isOptionSettingKey(settingKey) || !visibility.settings.appDefaults[settingKey]) {
    return <Redirect to="/settings" />;
  }

  const { title, description, options } = SETTING_DEFINITIONS[settingKey];
  const allOptions: SettingOption[] = [
    ...options,
    ...(settingKey === 'openingCasino' ? casinoOptions : []),
  ];

  const handleReset = () => {
    if (settings && defaults && isSameSettingValue(settings[settingKey], defaults[settingKey])) {
      setShowDefaultToast(true);
      return;
    }
    resetSettings([settingKey]);
  };

  return (
    <IonPage className="settings-page">
      <SettingsHeader />
      <IonContent className="settings-content">
        <div className="settings-options__intro">
          <h2 className="settings-group__title">{title}</h2>
          <p className="settings-options__description">{description}</p>
        </div>

        {settingKey === 'compareInsights' ? (
          <CompareInsightsPicker
            value={settings?.compareInsights ?? []}
            disabled={!settings}
            onChange={(insights) => updateSetting('compareInsights', insights)}
          />
        ) : (
          <div className="settings-card">
            {allOptions.map((option) => {
              const isSelected = settings?.[settingKey] === option.value;
              return (
                <button
                  key={option.value}
                  className="settings-row settings-row--option"
                  aria-pressed={isSelected}
                  disabled={!settings}
                  onClick={() => updateSetting(settingKey, option.value)}
                >
                  <span className="settings-row__label">{option.label}</span>
                  <span className="settings-row__check">
                    {isSelected && <img src="assets/icon/settings/check.svg" alt="Selected" />}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {visibility.appDefaultOptions.resetToDefault && (
          <button
            className="settings-card settings-action"
            disabled={!settings}
            onClick={handleReset}
          >
            <span className="settings-action__label">Reset to Default</span>
          </button>
        )}
      </IonContent>

      {showDefaultToast && <DefaultBehaviorToast onClose={closeDefaultToast} />}
    </IonPage>
  );
};

export default SettingOptions;
